import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ExternalLink } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeaturedCarousel = () => {
  const [publications, setPublications] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pubRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/featured-publications?visible_only=true`),
          fetch(`${API_URL}/api/carousel-settings`),
        ]);
        const pubs = await pubRes.json();
        const cfg = await settingsRes.json();
        setPublications(pubs.filter((p) => p.logo));
        setSettings(cfg);
        setLoaded(true);
      } catch {
        setLoaded(true);
      }
    };
    fetchData();
  }, []);

  if (!loaded || publications.length === 0) return null;

  return (
    <section
      className="py-12 md:py-16 px-6 md:px-8 bg-white"
      data-testid="featured-on-section"
      aria-label="Featured publications"
    >
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">
          As Featured In
        </h2>
        <CarouselInner publications={publications} settings={settings} />
      </div>
    </section>
  );
};

const CarouselInner = ({ publications, settings }) => {
  const slidesToShow = useResponsiveSlides(settings);
  const autoplayPlugin = settings?.autoplay
    ? [
        Autoplay({
          delay: settings.autoplay_speed || 3000,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
        }),
      ]
    : [];

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: settings?.loop ?? true,
      align: "start",
      slidesToScroll: 1,
      containScroll: false,
      dragFree: true,
    },
    autoplayPlugin
  );

  // Duplicate for seamless look when fewer items than slots
  const slides =
    publications.length < slidesToShow
      ? [...publications, ...publications, ...publications]
      : publications;

  const slideWidth = 100 / slidesToShow;

  return (
    <div
      className="overflow-hidden"
      ref={emblaRef}
      role="region"
      aria-roledescription="carousel"
      aria-label="Publication logos"
      tabIndex={0}
      onKeyDown={(e) => {
        if (!emblaApi) return;
        if (e.key === "ArrowLeft") emblaApi.scrollPrev();
        if (e.key === "ArrowRight") emblaApi.scrollNext();
      }}
    >
      <div className="flex">
        {slides.map((pub, i) => (
          <div
            key={`${pub.id}-${i}`}
            className="flex-shrink-0 px-4 md:px-6"
            style={{ width: `${slideWidth}%` }}
            role="group"
            aria-roledescription="slide"
            aria-label={pub.name}
          >
            <a
              href={pub.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center h-16 md:h-20"
              title={pub.name}
              data-testid={`featured-pub-${pub.id}`}
            >
              <img
                src={pub.logo}
                alt={pub.name}
                loading="lazy"
                decoding="async"
                className="h-full w-auto max-w-[160px] md:max-w-[200px] object-contain group-hover:scale-[1.03] transition-transform duration-300 ease-out"
              />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

function useResponsiveSlides(settings) {
  const [count, setCount] = useState(settings?.logos_desktop ?? 6);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setCount(settings?.logos_mobile ?? 2);
      else if (w < 1024) setCount(settings?.logos_tablet ?? 4);
      else setCount(settings?.logos_desktop ?? 6);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [settings]);

  return count;
}

export default FeaturedCarousel;
